
if (!customElements.get('vv-product-journey')) {
  customElements.define('vv-product-journey', class extends HTMLElement {
    connectedCallback() {
      this.life = new AbortController();
      const signal = this.life.signal;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      const restart = () => this.start();
      this.reduced.addEventListener('change', restart, { signal });
      this.mobile.addEventListener('change', restart, { signal });
      document.addEventListener('shopify:section:load', restart, { signal });
      document.addEventListener('shopify:section:reorder', restart, { signal });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this.hero)) this.stop();
      }, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (this.closest('.shopify-section') === event.target) this.scheduleMeasure();
      }, { signal });
      this.start();
    }
    start() {
      this.stop();
      if (!this.isConnected || this.dataset.enabled !== 'true' || this.reduced.matches) return;
      const wrapper = this.closest('.shopify-section');
      let previous = wrapper?.previousElementSibling;
      while (previous && !previous.classList.contains('shopify-section')) previous = previous.previousElementSibling;
      this.hero = previous?.querySelector('vv-immersive-hero');
      this.product = this.hero?.querySelector('.vv-hero__product');
      this.photo = this.product?.querySelector('img');
      if (!this.photo || this.hero.hasAttribute('data-journey-linked')) return;
      this.run = new AbortController();
      const signal = this.run.signal;
      if (!this.photo.complete || !this.photo.naturalWidth) {
        this.photo.addEventListener('load', () => this.start(), { once: true, signal });
        return;
      }
      this.parent = this.product.parentElement;
      this.next = this.product.nextSibling;
      this.portal = this.querySelector('.vv-journey__portal');
      this.target = this.querySelector('.vv-journey__target');
      this.hero.setAttribute('data-journey-linked', '');
      this.oldMotion = this.hero.dataset.motion;
      this.hero.dataset.motion = 'false';
      this.hero.style.setProperty('--vv-x', '0px');
      this.hero.style.setProperty('--vv-y', '0px');
      this.dataset.ready = 'true';
      this.measure();
      window.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleMeasure(), { passive: true, signal });
      window.addEventListener('pageshow', () => this.scheduleMeasure(), { signal });
      this.resize = new ResizeObserver(() => this.scheduleMeasure());
      this.resize.observe(this.hero);
      this.resize.observe(this.querySelector('.vv-journey__arrival'));
      document.fonts.ready.then(() => { if (!signal.aborted) this.scheduleMeasure(); });
      this.prepareFrames(signal);
    }
    scheduleMeasure() {
      if (!this.run || this.run.signal.aborted) return;
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => this.measure());
    }
    measure() {
      if (!this.product || !this.parent?.isConnected) return;
      if (this.origin) { this.origin.replaceWith(this.product); this.origin = null; }
      this.product.style.removeProperty('height');
      this.product.querySelector('picture').style.removeProperty('height');
      const rect = this.product.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const heroRect = this.hero.getBoundingClientRect();
      const target = this.target.getBoundingClientRect();
      const y = window.scrollY;
      this.geometry = {
        x: rect.left + rect.width / 2, y: rect.top + y + rect.height / 2,
        width: rect.width, height: rect.height,
        tx: target.left + target.width / 2, ty: target.top + y + target.height / 2,
        scale: Math.min(target.width / rect.width, target.height / rect.height),
        start: heroRect.top + y + Math.min(100, heroRect.height * .1),
        end: target.top + y + target.height / 2 - innerHeight * .43,
        mobile: this.mobile.matches
      };
      this.geometry.end = Math.max(this.geometry.start + 1, this.geometry.end);
      this.origin = document.createElement('div');
      this.origin.className = 'vv-hero__product vv-journey__origin';
      this.origin.style.height = `${rect.height}px`;
      this.origin.setAttribute('aria-hidden', 'true');
      this.product.replaceWith(this.origin);
      this.portal.append(this.product);
      this.portal.style.width = `${rect.width}px`;
      this.portal.style.height = `${rect.height}px`;
      this.product.style.height = `${rect.height}px`;
      this.product.querySelector('picture').style.height = `${rect.height}px`;
      this.draw();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.draw(); });
    }
    draw() {
      if (!this.geometry || !this.portal) return;
      const g = this.geometry;
      const y = window.scrollY;
      const p = Math.max(0, Math.min(1, (y - g.start) / (g.end - g.start)));
      const ease = p * p * (3 - 2 * p);
      const scale = 1 + (g.scale - 1) * ease + Math.sin(p * Math.PI) * (g.mobile ? .02 : .06);
      const x = g.x + (g.tx - g.x) * ease;
      const cy = g.y + (g.ty - g.y) * p - y;
      this.portal.style.transform = `translate3d(${x - g.width * scale / 2}px,${cy - g.height * scale / 2}px,0) scale(${scale})`;
      this.portal.style.setProperty('--vv-j-tilt', `perspective(1000px) rotateY(${Math.sin(p * Math.PI * 2) * (g.mobile ? 3 : 9)}deg) rotate(${-7 * (1 - ease)}deg)`);
      this.hero.style.setProperty('--vv-j-hero-opacity', String(1 - Math.min(1, p * 3)));
      const reveal = Math.max(0, Math.min(1, (p - .55) / .4));
      this.style.setProperty('--vv-j-reveal', String(reveal));
      this.style.setProperty('--vv-j-drift', `${(1 - reveal) * Number(this.dataset.parallax) * (g.mobile ? 1 : 3)}px`);
      if (this.images && this.context) {
        const index = Math.min(this.images.length - 1, Math.floor(ease * this.images.length));
        const active = p > 0 && p < 1;
        this.portal.dataset.frames = String(active);
        if (active && index !== this.lastIndex) {
          this.context.clearRect(0, 0, 1000, 1000);
          const im = this.images[index];
          const ratio = Math.min(1000 / im.naturalWidth, 1000 / im.naturalHeight);
          this.context.drawImage(im, (1000-im.naturalWidth*ratio)/2, (1000-im.naturalHeight*ratio)/2, im.naturalWidth*ratio, im.naturalHeight*ratio);
          this.lastIndex = index;
        }
      }
    }
    prepareFrames(signal) {
      if (this.dataset.sequence !== 'true' || this.mobile.matches || navigator.connection?.saveData) return;
      const sources = [...this.querySelector('template').content.querySelectorAll('[data-src]')].map(x => x.dataset.src);
      if (sources.length < 12) return;
      const observer = new IntersectionObserver(async entries => {
        if (!entries.some(x => x.isIntersecting)) return;
        observer.disconnect();
        const images = [];
        let cursor = 0;
        try {
          await Promise.all(Array.from({length:3}, async () => {
            while (cursor < sources.length && !signal.aborted) {
              const i = cursor++;
              const img = new Image(); img.decoding = 'async'; img.src = sources[i];
              await img.decode(); images[i] = img;
            }
          }));
          if (signal.aborted || images.length !== sources.length) return;
          this.images = images;
          this.canvas = document.createElement('canvas');
          this.canvas.width = 1000; this.canvas.height = 1000;
          this.canvas.className = 'vv-journey__canvas'; this.canvas.setAttribute('aria-hidden','true');
          this.product.querySelector('.vv-hero__float').append(this.canvas);
          this.context = this.canvas.getContext('2d'); this.schedule();
        } catch { this.images = null; }
      }, { rootMargin: '200px' });
      observer.observe(this.hero);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
    stop() {
      this.run?.abort(); this.resize?.disconnect();
      cancelAnimationFrame(this.frame); cancelAnimationFrame(this.measureFrame); this.frame = 0;
      this.canvas?.remove(); this.canvas = null; this.context = null; this.images = null; this.lastIndex = -1;
      if (this.origin && this.product) {
        this.product.style.removeProperty('height');
        this.product.querySelector('picture')?.style.removeProperty('height');
        this.origin.replaceWith(this.product);
      }
      if (this.hero?.hasAttribute('data-journey-linked')) {
        this.hero.removeAttribute('data-journey-linked');
        this.hero.style.removeProperty('--vv-j-hero-opacity');
        if (this.oldMotion !== undefined) this.hero.dataset.motion = this.oldMotion;
      }
      if (this.portal) { this.portal.removeAttribute('data-frames'); this.portal.style.cssText = ''; }
      this.origin = null; this.geometry = null; this.product = null;
      delete this.dataset.ready;
      this.style.removeProperty('--vv-j-reveal'); this.style.removeProperty('--vv-j-drift');
    }
    disconnectedCallback() { this.stop(); this.life?.abort(); }
  });
}
